import { type CreateFeatureFlag, featureFlagSchema, featureFlagUserSchema } from '@si/types';
import { get, patch, post } from '@si/utils';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';

import { queryKeys } from './queryKeys';
import { useUser } from './useUser';

const refreshOptions = {
  staleTime: 0,
  refetchOnWindowFocus: true,
  refetchOnMount: 'always' as const,
};

export function useFeatureFlags() {
  const { user } = useUser();
  return useQuery({
    queryKey: queryKeys.getFeatureFlags(user?.userId),
    queryFn: () => get({ url: '/feature-flags', schema: z.array(featureFlagSchema) }),
    enabled: !!user?.isSiAdmin,
    ...refreshOptions,
  });
}

// Use this for feature navigation, routes and content. Unknown keys or failed reads stay off.
// Backend operations must also enforce their existing authorization and relevant flag checks.
export function useFeatureFlag(key: string) {
  const { user } = useUser();
  const query = useQuery({
    queryKey: queryKeys.getEffectiveFeatureFlags(user?.userId),
    queryFn: () =>
      get({ url: '/feature-flags/current', schema: z.record(z.string(), z.boolean()) }),
    enabled: !!user,
    ...refreshOptions,
  });
  return {
    enabled: !!user && !query.isError && query.data?.[key] === true,
    isLoading: query.isLoading,
    error: query.error,
  };
}

export function useFeatureFlagUsers(key?: string) {
  const { user } = useUser();
  return useQuery({
    queryKey: queryKeys.getFeatureFlagUsers(key, user?.userId),
    queryFn: () =>
      get({
        url: `/feature-flags/${encodeURIComponent(key!)}/users`,
        schema: z.array(featureFlagUserSchema),
      }),
    enabled: !!key && !!user?.isSiOverlord,
    ...refreshOptions,
  });
}

export type FeatureFlagAction =
  | { type: 'create'; flag: CreateFeatureFlag }
  | { type: 'select'; key: string; enabled: boolean; userId?: number }
  | { type: 'release' | 'retire'; key: string };

export function useFeatureFlagActions() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (action: FeatureFlagAction) => {
      if (action.type === 'create') {
        await post({ url: '/feature-flags', body: action.flag });
      } else if (action.type === 'select') {
        await patch({
          url: `/feature-flags/${encodeURIComponent(action.key)}/users/${action.userId ?? 'current'}`,
          body: { enabled: action.enabled },
        });
      } else {
        await post({ url: `/feature-flags/${encodeURIComponent(action.key)}/${action.type}` });
      }
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: queryKeys.featureFlags }),
  });
}
